import type { Maybe, OptionalPromise } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { getCurrentUserServer, loadRawEntityDataByID, loadRawEntityDataByQuery } from '@adornis/bh-util/util.js';
import { FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import type { MailTemplate } from '@adornis/mails/db/mail-template.js';
import type { Recipient } from '@adornis/mails/db/recipients.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { Materialize } from '@adornis/materialized-views/decorators/materialize.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { bhBaseQlContext } from '../bh-context.js';
import type { TimeSlot } from '../datetime/time-slot.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { BHPayment } from './BHPayment.js';
import { Product } from './products.js';
import { UserStammdaten } from './stammdaten.js';
import { User } from './user.js';

// create indices
export async function ensureParticipationIndexes(): Promise<void> {
  const participationCollection = mongo().collection('participations');

  // Swallow IndexKeySpecsConflict (86) — option changes require an explicit drop+recreate migration.
  const createIndexSafe = async (
    keys: Parameters<typeof participationCollection.createIndex>[0],
    options?: Parameters<typeof participationCollection.createIndex>[1],
  ) => {
    try {
      await participationCollection.createIndex(keys, options);
    } catch (error) {
      if ((error as { code?: number })?.code === 86) return;
      // eslint-disable-next-line no-console
      console.warn('[participations] createIndex failed, continuing:', { keys, options, error });
    }
  };

  await createIndexSafe({ _class: 1 });
  await createIndexSafe({ userID: 1 });
  await createIndexSafe({ productID: 1 });
  await createIndexSafe({ state: 1 });
  await createIndexSafe({ userID: 1, productID: 1 }, { unique: true });
  await createIndexSafe(
    { productID: 1, waitingListPlace: 1 },
    { unique: true, partialFilterExpression: { waitingListPlace: { $type: 'number' } } },
  );

  // Compound indexes for the hot suspend/move query paths. calculateWorkshopLeftCapacity's count, the
  // nachrück waitlist query and getParticipationsByProductIDsAndState filter by (productID, state); the
  // suspend/cancel and active-participation paths filter by (userID, state). The single-field indexes
  // force the state predicate to be evaluated in-memory over every participation of a product/user.
  await createIndexSafe({ productID: 1, state: 1 });
  await createIndexSafe({ userID: 1, state: 1 });
}

// The admin participant/user dataview subscriptions (subscribeParticipationsByInstitution,
// subscribeParticipantsByInstitutionPaginated, subscribeUserDataview/subscribeUserDataviewCount) re-run a
// full aggregation over `participations-materialized` on EVERY write to participations/users/the
// materialized collection, once per connected admin session. Their first $match is on
// product.institutionID (+ product.timeframeID) and they $group by userID. Without these indexes each
// re-run is a COLLSCAN of the entire materialized collection — a primary driver of the suspend/move
// MongoDB CPU spike. The materialized-view library never indexes its view collections.
export async function ensureParticipationMaterializedIndexes(): Promise<void> {
  const collection = mongo().collection('participations-materialized');
  try {
    await collection.createIndex({ 'product.institutionID': 1, 'product.timeframeID': 1 });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[participations-materialized] createIndex failed, continuing:', error);
  }
  try {
    await collection.createIndex(
      { 'product.institutionID': 1, 'product.timeframeID': 1 },
      { name: 'pm_institution_timeframe_de', collation: { locale: 'de' } },
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[participations-materialized] createIndex failed, continuing:', error);
  }
  try {
    await collection.createIndex({ userID: 1 });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[participations-materialized] createIndex failed, continuing:', error);
  }
}

@Entity()
export class SimplifiedPrice extends AdornisEntity {
  static override _class = 'SimplifiedPrice';

  @Field(type => Int)
  unitAmount!: number;

  @Field(type => String)
  currency!: string;
}

@Materialize({
  selectionSet: async () => {
    const { Suspension } = await import('./suspension.js');
    const { UserStammdaten } = await import('./stammdaten.js');
    const { Appointment } = await import('./appointment.js');
    const { SimplifiedPrice } = await import('./Participation.js');

    return {
      userID: 1,
      user: {
        email: 1,
        username: 1,
        title: 1,
        firstName: 1,
        lastName: 1,
        dateOfBirth: 1,
        stripeCustomerID: 1,
        suspentions: selectionSet(() => Suspension, 1),
        whatsappNotification: 1,
        whatsappPhone: 1,
        receivesNewsletter: 1,
      },
      userStammdaten: selectionSet(() => UserStammdaten, 3),
      productID: 1,
      product: {
        name: 1,
        prices: { unitAmount: 1 },
        sortingWeight: 1,
        hasPrice: 1,
        category: { name: 1 },
        institutionID: 1,
        timeframeID: 1,
        timeframe: { _id: 1, name: 1 },
        productCopyID: 1,
        publishDate: 1,
        topic: 1,
        language: 1,
        presenceType: 1,
        isCanceled: 1,
        capacity: 1,
        appointments: selectionSet(() => Appointment, 1),
        slotConfig: { start: 1 },
      },
      createdAt: 1,
      updatedAt: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, discount: 1, amount: 1 },
      state: 1,
      waitingListPlace: 1,
      waitingListPriority: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      isNoShow: 1,
      productPriceForUser: selectionSet(() => SimplifiedPrice, 1),
      fulfilledParticipationRequirements: 1,
      registrationDate: 1,
      signoffDate: 1,
      nachrueckPriority: 1,
      appointmentParticipations: { _id: 1, appointmentID: 1, date: 1 },
      registrationDateThroughAdmin: 1,
      signoffDateThroughAdmin: 1,
      ikh_doNotSendInvoice: 1,
    } as any;
  },
})
@Entity()
export class Participation<Tproduct extends Product = Product> extends MongoEntity {
  public static override _class = 'Participation';
  public static override _collectionName = 'participations';

  @validate(nonOptional())
  @Field(type => ID)
  userID!: string;

  @DataLoadedField(type => User, participation => ({ _id: participation.userID as any }))
  user: Maybe<User>;

  @Field(type => UserStammdaten, {
    resolve: function (this: Participation) {
      return async (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
        const requestingUser = await getCurrentUserServer();
        if (!context.serverContext && !requestingUser) throw new Error('you are not authenticated');
        if (this.userStammdaten) return this.userStammdaten;

        const userID = this.userID;
        if (!userID) throw new Error('due to performance reasons you have to provide the userID');
        const productID = this.productID;
        if (!productID) throw new Error('due to performance reasons you have to provide the productID');
        const institutionID = await (async () => {
          const product = await loadRawEntityDataByID(Product, productID, { institutionID: 1 });
          if (!product) throw new Error('product not found ' + this.productID);
          return product.institutionID;
        })();

        (() => {
          if (context.serverContext) return;
          if (requestingUser!._id === userID) return;
          if (requestingUser?.isSuperAdmin) return;
          if (requestingUser?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, institutionID)) return;
          throw new Error('not authorized');
        })();

        const result = await loadRawEntityDataByQuery(UserStammdaten, { userID, institutionID }, gqlFields);
        const stammdatenData = result[0];
        if (!stammdatenData) return null;

        return constructValue(stammdatenData) as UserStammdaten;
      };
    },
  })
  userStammdaten: Maybe<UserStammdaten>;

  @validate(nonOptional())
  @Field(type => ID)
  productID!: string;

  @DataLoadedField(type => Product, participation => ({ _id: participation.productID as any }))
  product: Maybe<Tproduct>;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(type => Date, { default: v => v ?? new Date() })
  updatedAt!: Date;

  @Field(type => BHPayment, {
    resolve: function (this: Participation) {
      return async () => {
        const m = await import('../api/participation.js');
        return m.getParticipationCurrentPaymentServer(this._id) as any;
      };
    },
  })
  currentPayment: Maybe<BHPayment>;

  @validate(options => {
    if (!options.value) throw new ValidationError('state must be set', { key: options.key, instance: options.target });
  })
  @Field(type => String, { default: value => value ?? ParticipationState.in })
  state!: ParticipationState;

  @Field(type => Boolean, { default: value => value ?? false })
  lastStateChangeWasInitiatedByAdmin: Maybe<boolean>;

  @Field(type => Int)
  waitingListPlace: Maybe<number>;

  // Per-participant edge-trigger guard for the "Nachrückplatz verfügbar" mail: set true when this
  // waitlister has been notified that a manual nachrück spot is available, reset to false once no
  // such spot is left. Lives on the participation (not the workshop) so a TN who joins the waiting
  // list while a spot is already open still gets notified, while nobody is notified twice per window.
  @Field(type => Boolean, { default: v => v ?? false })
  nachrueckplatzVerfuegbarNotified: Maybe<boolean>;

  @Field(type => String)
  nachrueckPriority: Maybe<'high' | 'low' | 'entry_requirements_not_met'>;

  @Field(type => Date)
  gotCertificate: Maybe<Date>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(type => Boolean, {
    resolve: function (this: Participation) {
      return () => {
        const isIn = this.state === ParticipationState.in;
        return isIn && this.state !== ParticipationState.participated;
      };
    },
  })
  isNoShow: Maybe<boolean>;

  @Field(type => SimplifiedPrice, {
    resolve: function (this: Participation) {
      return async () => {
        const { getProductPriceForUser } = await import('../api/participation.js');
        const price = await getProductPriceForUser(this.productID, this.userID);
        if (!price) return null;
        return new SimplifiedPrice({ unitAmount: price.unitAmount, currency: price.currency });
      };
    },
  })
  productPriceForUser: Maybe<SimplifiedPrice>;

  @Field(type => [String], { default: v => v ?? [] })
  fulfilledParticipationRequirements: Maybe<string[]>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(type => Date, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.lastStateChangeWasInitiatedByAdmin) return null;
        return this.createdAt;
      };
    },
  })
  registrationDate: Maybe<Date>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(type => Date, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.state === ParticipationState.left || this.lastStateChangeWasInitiatedByAdmin) return null;
        return this.updatedAt;
      };
    },
  })
  signoffDate: Maybe<Date>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(type => Date, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.lastStateChangeWasInitiatedByAdmin) return this.createdAt;
        return null;
      };
    },
  })
  registrationDateThroughAdmin: Maybe<Date>;

  @Dependencies({ classes: () => [], ids: () => [] })
  @Field(type => Date, {
    resolve: function (this: Participation) {
      return async () => {
        if (this.state === ParticipationState.left && this.lastStateChangeWasInitiatedByAdmin) return this.updatedAt;
        return null;
      };
    },
  })
  signoffDateThroughAdmin: Maybe<Date>;

  @Field(type => Boolean)
  ikh_doNotSendInvoice?: boolean;

  static override async transformBeforeUpdate(oldEntity: PartialEntityData<Participation>, update: Participation) {
    update.lastStateChangeWasInitiatedByAdmin = !!bhBaseQlContext.participationStateChangeWasInitiatedByAdmin;
    update.updatedAt = new Date();

    super.transformBeforeUpdate(oldEntity, update);
  }

  /**
   * @deprecated use getParticipationTimespan instead
   */
  public async timespan(): Promise<NarrowedEntityData<TimeSlot, { _id: 1; start: 1; end: 1; isNonBlocking: 1 }>[]> {
    throw new Error('this method should be overridden');
  }

  override toString() {
    return `${this.user?.email} -> ${this.product?.name ?? '<Titel>'} (${this.state})`;
  }
}

export type EmailEvent = [Recipient, MailTemplate, Record<string, any>, string[]];

@Entity()
export class ParticipationParticipatedFieldMappingEntry extends FieldMappingEntry<
  boolean,
  PartialEntityData<Participation>
> {
  static override _class = 'ParticipationParticipatedFieldMappingEntry';

  constructor(
    doc: PartialEntityData<FieldMappingEntry>,
    public override deserialize = (
      value: string | undefined,
      data: Record<string, string>,
      i: number,
      entityInitializer: PartialEntityData<Participation>,
    ): OptionalPromise<boolean | null> => /ja/i.test(value?.trim() ?? ''),
  ) {
    super(doc, deserialize);
  }
}
